import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { ChatBubble } from '@material-ui/icons';
import { CommentBox, PostCommentType } from './CommentBox';
import { CommentViewer } from './CommentViewer';
import { useSaveComment } from './Comment';
import { IComment } from './types';
import { useCampfireLazyQuery } from '../../global/network/useCampfireLazyQuery';
import { GET_ROSTER_COMMENTS, GET_ROSTER_TEMPLATE_COMMENTS } from './roster-comments-model.gql';
import { ActivityHeader } from '../../screens/general/activities-v2/ActivitySidebar/ActivityHeader';
import { useCampfireTheme } from '../../theme/useCampfireTheme';

interface RosterCommentsProps {
  activityId: string;
  activityDate: string;
  rosterTemplateId?: string;
  compactInput?: boolean;
}

export function RosterComments({ activityId, activityDate, rosterTemplateId, compactInput }: RosterCommentsProps) {
  const saveComment = useSaveComment();
  const { theme } = useCampfireTheme();
  const [showCommentBox, setShowCommentBox] = useState(false);

  const [getRosterComments, { data: rosterCommentsData, refetch: refetchRoster }] = useCampfireLazyQuery(
    GET_ROSTER_COMMENTS
  );

  const [getRosterTemplateComments, { data: templateCommentsData, refetch: refetchTemplate }] = useCampfireLazyQuery(
    GET_ROSTER_TEMPLATE_COMMENTS
  );

  React.useEffect(() => {
    if (rosterTemplateId) {
      getRosterTemplateComments({ variables: { rosterTemplateId } });
    } else {
      getRosterComments({ variables: { activityId, activityDate } });
    }
  }, [activityId, activityDate, rosterTemplateId]);

  const comments: IComment[] = useMemo(() => {
    if (rosterTemplateId) {
      return (templateCommentsData?.vm?.rosterTemplateComments ?? []) as IComment[];
    }
    return (rosterCommentsData?.vm?.rosterComments ?? []) as IComment[];
  }, [rosterCommentsData, templateCommentsData, rosterTemplateId]);

  const refetch = rosterTemplateId ? refetchTemplate : refetchRoster;

  const onPost = (body: PostCommentType) => {
    const params = rosterTemplateId ? { rosterTemplateId, ...body } : { activityId, activityDate, ...body };
    saveComment(params).then(() => {
      if (refetch) refetch();
      if (compactInput) setShowCommentBox(false);
    });
  };

  const handleEdit = (body: PostCommentType) => {
    saveComment(body).then(() => {
      if (refetch) refetch();
    });
  };

  const onReply = (parentId: string, content: string) => {
    const params = rosterTemplateId
      ? { rosterTemplateId, parentId, content }
      : { activityId, activityDate, parentId, content };
    saveComment(params).then(() => {
      if (refetch) refetch();
    });
  };

  const rootComments = comments.filter((comment) => !comment.parentId);
  const nonRootComments = comments.filter((comment) => Boolean(comment.parentId));

  const showPlaceholder = compactInput && !showCommentBox;

  return (
    <Box mt={3}>
      <ActivityHeader title='Comments' />
      {showPlaceholder ? (
        <Box
          mt={2}
          onClick={() => setShowCommentBox(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 14px',
            borderRadius: '6px',
            cursor: 'pointer',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }}
        >
          <ChatBubble
            style={{
              fontSize: '16px',
              color: theme.color.grey.neutral300,
              marginRight: '10px',
            }}
          />
          <Typography
            style={{
              color: theme.color.grey.neutral400,
              fontSize: '14px',
            }}
          >
            Write a comment...
          </Typography>
        </Box>
      ) : (
        <Box mt={2}>
          <CommentBox onPost={onPost} enableAttachment autoFocus />
        </Box>
      )}
      <Box paddingTop='1em'>
        {rootComments.map(
          (comment) =>
            !comment.removedDate && (
              <CommentViewer
                key={comment.commentId}
                comment={comment}
                comments={nonRootComments}
                onReply={onReply}
                refetch={refetch}
                handleEdit={handleEdit}
                enableAttachment
              />
            )
        )}
      </Box>
    </Box>
  );
}
